import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Loader2, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const MEMBER_COLORS = [
  "bg-pink-100 text-pink-600",
  "bg-purple-100 text-purple-600",
  "bg-rose-100 text-rose-600",
  "bg-emerald-100 text-emerald-600",
  "bg-sky-100 text-sky-600",
  "bg-amber-100 text-amber-600",
];

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/org/members`);
      setMembers(data.members || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load team members");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`/org/members`, form);
      toast.success(`Team member "${form.email}" added`);
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add member");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove login for "${m.email}"? They will lose access immediately.`)) return;
    setBusy((b) => ({ ...b, [m.id]: true }));
    try {
      await axios.delete(`/org/members/${m.id}`);
      toast.success(`Removed ${m.email}`);
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not remove member");
    } finally {
      setBusy((b) => ({ ...b, [m.id]: false }));
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Team</h1>
          <p className="text-xs font-mono text-slate-400 uppercase tracking-widest mt-1">
            Everyone here shares this workspace's reports and data
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl"
          data-testid="team-add-btn"
        >
          <Plus size={15} className="mr-1.5" /> Add Member
        </Button>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.03),0_4px_12px_-2px_rgba(15,23,42,0.04)]">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Users size={14} className="text-pink-600" /> Members
          </h2>
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
            {members.length} total
          </span>
        </div>
        {loading ? (
          <div className="text-xs text-slate-400 font-mono p-6">Loading…</div>
        ) : (
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50 text-slate-400 font-mono uppercase text-[10px] tracking-wider">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-3 py-3 font-medium">Email</th>
                <th className="px-3 py-3 font-medium">Added</th>
                <th className="px-3 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, i) => (
                <tr
                  key={m.id}
                  className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors h-14"
                  data-testid={`team-member-${m.id.slice(0, 8)}`}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${MEMBER_COLORS[i % MEMBER_COLORS.length]}`}>
                        {(m.name || m.email || "?")[0].toUpperCase()}
                      </div>
                      <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        {m.name || m.email.split("@")[0]}
                        {m.id === user?.id && (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-pink-50 text-pink-600 border border-pink-200">
                            You
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-mono text-[11px] text-slate-500">{m.email}</td>
                  <td className="px-3 py-3 font-mono text-[11px] text-slate-500">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {m.id !== user?.id && (
                      <Button
                        onClick={() => remove(m)}
                        disabled={busy[m.id]}
                        variant="outline"
                        className="h-8 w-8 p-0 bg-white border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-400"
                        title="Remove this login"
                        data-testid={`team-delete-${m.id.slice(0, 8)}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Creates a login with full access to this workspace's reports, runs and exports.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {[
              { k: "name", label: "Display name", placeholder: "e.g. Priya Sharma" },
              { k: "email", label: "Login email", placeholder: "teammate@company.com", type: "email" },
              { k: "password", label: "Password (min 8 chars)", placeholder: "••••••••", type: "password" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1 block">
                  {f.label}
                </label>
                <Input
                  type={f.type || "text"}
                  value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="bg-slate-50 border-slate-200 font-mono text-xs text-slate-900"
                  placeholder={f.placeholder}
                  data-testid={`team-${f.k}-input`}
                />
              </div>
            ))}
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !form.email || form.password.length < 8}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-40"
                data-testid="team-create-submit"
              >
                {submitting ? (
                  <><Loader2 size={12} className="mr-1.5 animate-spin" /> Adding…</>
                ) : "Add member"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
